import { FiGlobe } from 'react-icons/fi';

export const accentTrainingData = {
  courseId: 'accent-training',
  courseType: 'listening-course',
  title: 'Accent Training',
  icon: FiGlobe,
  overview: {
    impact: 'Prepare for various English accents in IELTS',
    focus: 'Understanding British, American, Australian accents',
    uses: 'All listening sections',
    duration: '25 minutes',
  },
  sections: [
    {
      title: 'Accents in IELTS Listening',
      content: `<p>IELTS uses a variety of native English accents. You may hear:</p>`,
      points: [
        'British English (most common)',
        'Australian English',
        'North American English',
        'New Zealand English',
        'Occasionally other accents (South African, Irish)',
      ],
      warning: 'Don\'t panic if an accent sounds unfamiliar. Focus on the content, not the accent.',
    },
    {
      title: 'British vs American Pronunciation',
      content: `<p>Key differences to be aware of:</p>`,
      table: {
        headers: ['Feature', 'British', 'American'],
        rows: [
          ['R sound', 'Often silent: "car" = /kɑː/', 'Pronounced: "car" = /kɑːr/'],
          ['T sound', 'Clear T: "water"', 'Soft D: "wader"'],
          ['A sound', '"Bath" with long A', '"Bath" with short A'],
          ['O sound', '"Hot" = /hɒt/', '"Hot" = /hɑːt/'],
          ['Schedule', '/ˈʃedjuːl/', '/ˈskedʒuːl/'],
        ],
      },
    },
    {
      title: 'Australian English Features',
      content: `<p>Australian English has distinctive features:</p>`,
      points: [
        'Rising intonation at end of statements (sounds like questions)',
        '"Today" sounds like "to die" — /təˈdaɪ/',
        'Shortened words: "arvo" (afternoon), "uni" (university)',
        'Relaxed pronunciation: "mate" instead of clear consonants',
      ],
      examples: [
        { label: 'Australian', text: '"G\'day! How are ya goin\' today?"' },
        { label: 'British', text: '"Good day! How are you doing today?"' },
      ],
    },
    {
      title: 'Strategies for Unfamiliar Accents',
      content: `<p>When you encounter an unfamiliar accent:</p>`,
      points: [
        'Don\'t panic — your ear will adjust after a few seconds',
        'Focus on stressed words which carry main meaning',
        'Use context to fill in words you miss',
        'Pay attention to key content words, not every word',
        'Trust the pauses and intonation patterns',
      ],
    },
  ],
  exercises: [
    {
      title: 'Accent Awareness',
      type: 'select',
      instructions: 'Which pronunciation pattern matches which accent?',
      questions: [
        {
          text: 'The "r" in "teacher" is not pronounced.',
          options: ['British', 'American', 'Both'],
          answer: 'British',
          explanation: 'British English drops R at end of syllables (non-rhotic).',
        },
        {
          text: '"Schedule" sounds like "skedule" with a K sound.',
          options: ['British', 'American', 'Australian'],
          answer: 'American',
          explanation: 'Americans pronounce "sch" as /sk/, British as /ʃ/.',
        },
        {
          text: 'Rising intonation at the end of statements.',
          options: ['British', 'American', 'Australian'],
          answer: 'Australian',
          explanation: 'Australian English often has this questioning intonation.',
        },
        {
          text: '"Water" sounds more like "wader" with a soft T.',
          options: ['American', 'British', 'Australian'],
          answer: 'American',
          explanation: 'Americans often soften the T to a D sound.',
        },
        {
          text: '"Can\'t" sounds like "kahnt" with a long A.',
          options: ['British', 'American', 'Australian'],
          answer: 'British',
          explanation: 'British uses long A, American uses short A (sounds like "cant").',
        },
      ],
    },
    {
      title: 'Word Variations',
      type: 'select',
      instructions: 'Match the word to the accent most likely to use it.',
      questions: [
        {
          text: '"Rubbish" for garbage/trash',
          options: ['British', 'American', 'Both'],
          answer: 'British',
          explanation: 'Americans typically say "trash" or "garbage".',
        },
        {
          text: '"Uni" for university',
          options: ['British', 'American', 'Australian'],
          answer: 'Australian',
          explanation: 'Australians commonly use this abbreviation.',
        },
        {
          text: '"Fortnight" for two weeks',
          options: ['British/Australian', 'American', 'All'],
          answer: 'British/Australian',
          explanation: 'Americans rarely use "fortnight".',
        },
        {
          text: '"Lift" for elevator',
          options: ['British', 'American', 'Both'],
          answer: 'British',
          explanation: 'Americans say "elevator," British say "lift."',
        },
        {
          text: '"Arvo" for afternoon',
          options: ['Australian', 'British', 'American'],
          answer: 'Australian',
          explanation: 'This is an Australian slang abbreviation.',
        },
      ],
    },
    {
      title: 'Accent Strategies',
      type: 'select',
      instructions: 'What should you do when you hear an unfamiliar accent?',
      questions: [
        {
          text: 'You hear a strong accent and feel confused. You should:',
          options: ['Stay calm—your ear will adjust', 'Panic and lose focus', 'Ignore the audio'],
          answer: 'Stay calm—your ear will adjust',
          explanation: 'After a few seconds, your brain adapts to the accent.',
        },
        {
          text: 'To understand different accents, the BEST strategy is:',
          options: ['Focus on stressed words and content words', 'Try to understand every single word', 'Only listen to one accent type'],
          answer: 'Focus on stressed words and content words',
          explanation: 'Stressed words carry the main meaning.',
        },
        {
          text: 'Before your IELTS test, you should:',
          options: ['Practice with various accent recordings', 'Only practice with one accent', 'Avoid listening practice'],
          answer: 'Practice with various accent recordings',
          explanation: 'IELTS uses multiple accents—prepare for all.',
        },
      ],
    },
    {
      title: 'Vocabulary Differences',
      type: 'fill-in',
      instructions: 'Write the British English equivalent.',
      questions: [
        {
          text: 'American: "apartment" → British: ___',
          answer: 'flat',
          explanation: 'British English uses "flat" for apartment.',
        },
        {
          text: 'American: "truck" → British: ___',
          answer: 'lorry',
          explanation: 'British English uses "lorry" for truck.',
        },
        {
          text: 'American: "sidewalk" → British: ___',
          answer: 'pavement',
          explanation: 'British English uses "pavement" for sidewalk.',
        },
      ],
    },
  ],
  tips: [
    'Expose yourself to various accents through podcasts and videos',
    'Practice with BBC, CNN, and Australian news',
    'Don\'t try to understand every word—focus on meaning',
    'Get comfortable with the IELTS audio pace',
  ],
  dailyPractice: [
    'Listen to 10 minutes of British English (BBC)',
    'Listen to 10 minutes of Australian English (ABC)',
    'Listen to 10 minutes of American English (NPR)',
    'Practice IELTS tests with various accent recordings',
  ],
  backPath: '/exams/english/#listening',
  backLabel: 'Back to Listening',
  aiContext: 'Accent Training for IELTS: Understanding different English accents including British, American, and Australian, with strategies for handling unfamiliar pronunciations.',
};

// Difficulty-variant factory: returns content adapted to the given difficulty level.
// difficulty 1-2: simplified content with basic examples and easier exercises
// difficulty 3: base content (default)
// difficulty 4-5: advanced content with complex examples and harder exercises
export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...accentTrainingData,
      title: accentTrainingData.title + ' (Foundations)',
      overview: {
        ...accentTrainingData.overview,
        duration: '15-20 minutes',
      },
      // Simplified: show only first 2 sections and first exercise set
      sections: accentTrainingData.sections.slice(0, Math.max(2, Math.ceil(accentTrainingData.sections.length / 2))),
      exercises: accentTrainingData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...accentTrainingData,
      title: accentTrainingData.title + ' (Advanced)',
      overview: {
        ...accentTrainingData.overview,
        duration: '30-35 minutes',
      },
      // Advanced: all sections plus advanced tips context
      sections: accentTrainingData.sections,
      exercises: accentTrainingData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(accentTrainingData.tips || []),
        'For Band 8+: Focus on natural-sounding usage rather than just accuracy.',
        'Advanced tip: Integrate this concept fluidly in timed IELTS writing tasks.',
      ],
    };
  }
  return accentTrainingData;
}
